import {Injectable, OnDestroy} from '@angular/core';
import {BehaviorSubject, defer, distinctUntilChanged, firstValueFrom, Observable, Subscription, timeout} from 'rxjs';
import {CommandService} from '@core/services/command.service';
import {SocketClusterService} from '@core/services/socket-cluster.service';
import {PlatformCommand, PlatformCommandResponse} from '@shared/commands/command-base';
import {EventStatus} from '@shared/models/notification-message.model';
import {CallMessageType} from '../call.service';

/**
 * The user's "Do not disturb for calls" switch (ITP-E-171-04, D-15). While it is on, the server sends every call
 * to this user straight to missed: nothing rings, the caller sees Unavailable, and the missed-call banner shows.
 * Read once from GetCallPreferencesCommand on first subscribe, set with SetCallDoNotDisturbCommand, and kept in
 * step across the user's tabs by CallPreferencesChanged on the user channel. It folds into ITP-P-33 presence
 * when that ships.
 */
@Injectable({providedIn: 'root'})
export class CallPreferencesService implements OnDestroy {
  static readonly commandTimeoutMs = 30000;

  private readonly doNotDisturbSubject = new BehaviorSubject<boolean>(false);
  private loadRequested = false;
  private readonly messages: Subscription;
  /** Whether Do not disturb for calls is on; false until the server says otherwise. */
  readonly doNotDisturb$: Observable<boolean> = defer(() => {
    this.load().then();
    return this.doNotDisturbSubject.pipe(distinctUntilChanged());
  });

  constructor(private commandService: CommandService,
              private socketClusterService: SocketClusterService) {
    this.messages = this.socketClusterService.onCallMessage().subscribe(message => this.onMessage(message));
  }

  get doNotDisturb(): boolean {
    return this.doNotDisturbSubject.value;
  }

  /** Asks the server once per page load. */
  async load(): Promise<void> {
    if (this.loadRequested) {
      return;
    }
    this.loadRequested = true;
    const response = await this.execute('GetCallPreferencesCommand', {});
    if (CallPreferencesService.succeeded(response)) {
      this.doNotDisturbSubject.next(response.fieldValues['DoNotDisturb'] === true);
    }
  }

  /** Turns the switch on or off. Shown at once; put back if the server refuses. */
  async setDoNotDisturb(on: boolean): Promise<void> {
    const before = this.doNotDisturb;
    this.doNotDisturbSubject.next(on);
    const response = await this.execute('SetCallDoNotDisturbCommand', {DoNotDisturb: on});
    if (!CallPreferencesService.succeeded(response)) {
      this.doNotDisturbSubject.next(before);
      return;
    }
    this.doNotDisturbSubject.next(response.fieldValues['DoNotDisturb'] === true);
  }

  toggle(): Promise<void> {
    return this.setDoNotDisturb(!this.doNotDisturb);
  }

  ngOnDestroy(): void {
    this.messages.unsubscribe();
  }

  private onMessage(notification: any): void {
    // The publisher may nest the message under Payload, as the websocket layer allows for.
    const message = notification?.messageType || notification?.MessageType
      ? notification
      : (notification?.Payload || notification?.payload || notification);
    const messageType = message?.messageType || message?.MessageType;
    if (messageType !== CallMessageType.PreferencesChanged) {
      return;
    }
    const value = message.doNotDisturb ?? message.DoNotDisturb;
    if (typeof value === 'boolean') {
      this.doNotDisturbSubject.next(value);
    }
  }

  private static succeeded(response: PlatformCommandResponse): boolean {
    return !!response && response.status !== EventStatus.Failed && !!response.fieldValues;
  }

  private async execute(name: string, fieldValues: Record<string, any>): Promise<PlatformCommandResponse> {
    const command = new PlatformCommand(name, 'CollaborationApi');
    Object.entries(fieldValues).forEach(([key, value]) => command.fieldValues[key] = value);
    command.waitForResponse = true;
    try {
      // executePlatformCommand does not error on a transport failure, so a bounded wait stands in for one.
      return await firstValueFrom(
        this.commandService.executePlatformCommand(command).pipe(timeout(CallPreferencesService.commandTimeoutMs)),
        {defaultValue: null});
    } catch {
      return null;
    }
  }
}
